import { BaseModel } from '@/models/BaseModel';
import { ActionData } from '@/typings/interfaces/data/settings/actions';
import { ActionType } from '@/typings/enums/enums';
import store from '@/store';
import useFlow from '@/hooks/useFlow';
import { events } from '@/services/events';
import { isSsr } from '@/utilities/Utilities';
import { redirect, RedirectTarget } from '@/services/url';
import { FlowPageDoesNotExists } from '@/exceptions/FlowPageDoesNotExists';
import { ReplacementTags } from '@/store/modules/global/state';
import { SdkActionEvent } from '@/sdk/events/actionEvent';
import { sdkEvents } from '@/sdk';
import { GameCampaignLinkModel } from '@/components/games/campaign-link/Model';
import { AddonLfButtonModel } from '@/components/addons/lf-button/Model';
import { AddonCountdownModel } from '@/components/addons/countdown/Model';
import { AddonLfIconModel } from '@/components/addons/lf-icon/Model';
import { FlowpageModel } from '@/components/layout/FlowpageModel';
import { ModelTypes } from '@/typings/types/types';

export type TriggerActionOrigin =
  | FlowpageModel
  | AddonLfButtonModel
  | GameCampaignLinkModel
  | AddonCountdownModel
  | AddonLfIconModel
  | undefined;

export interface ActionModelState {
  url?: string | null;
  type: ActionType;
  target?: string;
  flowPage?: number | null;
  section?: string | null;
  popover?: string | null;
  onClickEvent?: string | null;
  delay?: number | null;
  keybinding?: string | null;
  autoTrigger?: number;
  dontShowPopupAfter: boolean;
  evaluating?: boolean;
}

export default class ActionsModel extends BaseModel<ActionData, ActionModelState> {
  private timer: number | undefined;

  parse(data: ActionData) {
    const state = this.state;
    state.url = data.url || null;
    state.target = data.target || '_blank';
    state.type = data.type || null;
    state.section = data.section || null;
    if (data.popover) {
      state.popover = data.popover;
    } else if (data.id) {
      state.popover = data.id;
    }
    state.onClickEvent = data.event || null;
    state.delay = data.delay || null;
    state.flowPage = data.flow_page ? Number(data.flow_page) : null;
    state.keybinding = data.keybinding || null;
    state.autoTrigger = data.auto_trigger ? Number(data.auto_trigger) * 1000 : undefined;
    state.dontShowPopupAfter = data.dont_show_popup_after === '1';
  }

  /**
   * We cannot start the timer in our construct as the element containing the actions model
   * might not be rendered yet. We'll leave it up to the components that utilize ActionsModel
   * to manually register the auto trigger.
   *
   * The callback parameter is options. This is used to overwrite the "trigger". Use cases ca be if
   * you manually call the triggerAction function but wishes to do something before triggering.
   */
  async startAutoTrigger(callback?: () => void): Promise<void> {
    if (!isSsr() && typeof this.timer === 'undefined' && this.state.autoTrigger) {
      this.timer = window.setTimeout(async () => {
        if (callback) {
          callback();
        } else {
          await this.triggerAction();
        }

        this.timer = undefined;
      }, this.state.autoTrigger);
    }
  }

  /**
   *
   * @param origin used in SDK to tell where action is invoked
   * @param replacementTags used in type URL - to apply replacement tags in path.
   * @returns Promise<void>
   */

  async triggerAction(origin?: ModelTypes, replacementTags?: ReplacementTags): Promise<void> {
    const startTime = new Date().getTime();

    if (!isSsr() && typeof this.timer !== 'undefined') {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }

    const campaignState = store.getters['campaign/state'];

    // Disable click events if button is disabled or if we're in edit mode...
    if (this.state.evaluating || campaignState.isEditModeActive) {
      return;
    }

    this.state.evaluating = true;

    if (origin) {
      const sdkActionEvent = new SdkActionEvent(this, origin);
      if (sdkActionEvent) {
        sdkEvents.emit('action', sdkActionEvent);

        try {
          await sdkActionEvent.waitForPromises();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[SDK] Action exception caught', e);
        }

        if (sdkActionEvent.defaultPrevented) {
          this.state.evaluating = false;
          return;
        }
      }
    }

    const delay = this.state.delay ?? 0;

    const actionCallback = () => {
      const state = this.state;

      switch (state.type) {
        case ActionType.URL:
          if (state.url) {
            this.goToUrl(state.url, replacementTags);
          }
          break;
        case ActionType.RESTART:
          window.location.reload();
          break;
        case ActionType.SECTION:
          if (state.section) {
            this.goToSection(state.section);
          }
          break;
        case ActionType.POPOVER:
          store.commit('campaign/setPopoverId', state.popover);
          break;
        case ActionType.CLOSE_POPOVER:
          store.commit('campaign/setPopoverId', state.popover);
          break;
        case ActionType.GOTO_FLOW_PAGE:
          this.goToFlowPage(this.state.flowPage);
          break;
        case ActionType.ONCLICK:
          if (state.onClickEvent) {
            this.onClick(state.onClickEvent);
          }
          break;
        case ActionType.NEXTFLOW:
          useFlow().goToNextFlowPage();
          break;
        case ActionType.FLOWPAGE:
          this.goToFlowPage(this.state.flowPage);
          break;
        case ActionType.FORCE_SKIP_REGISTRATION:
          this.onForceSkipRegistration(state.flowPage);
          break;
        default:
          break;
      }
      this.state.evaluating = false;
    };

    if (delay) {
      setTimeout(() => {
        actionCallback();
        /**
         * Delay minus the execution of the function in miliseconds.
         */
      }, Math.max(delay * 1000, new Date(new Date().getTime()).getTime() - startTime));
    } else {
      actionCallback();
    }

    // Allow others to hook in and add custom action behaviors.
    events.emit('invalidAction', this);
  }

  goToUrl(url: string, replacementTags?: ReplacementTags) {
    let target: RedirectTarget | undefined;

    switch (this.state.target) {
      case '_blank':
        target = RedirectTarget.BLANK;
        break;

      case '_top':
        target = RedirectTarget.TOP;
        break;

      case '_self':
        target = RedirectTarget.SELF;
        break;
    }
    redirect(
      {
        url: url,
        target: target ?? RedirectTarget.BLANK
      },
      replacementTags
    );
  }

  goToSection(id: string) {
    const el = document.querySelector(`.section--original-${id}`);

    if (el) {
      el.scrollIntoView({
        behavior: 'smooth'
      });
      return;
    }

    throw new Error(`cant find HTML element with selector .section--original-${id}`);
  }

  goToFlowPage(flowPage?: number | null) {
    if (!flowPage) {
      useFlow().goToNextFlowPage();
    }

    if (flowPage) {
      try {
        useFlow().makeSpecificFlowPageActive(flowPage);
      } catch (e) {
        if (e instanceof FlowPageDoesNotExists) {
          useFlow().goToNextFlowPage();
        } else {
          throw e;
        }
      }
    }
  }

  onClick(eventName: string) {
    const fnStr = eventName.match(/(.+)(\(.*\))/);

    if (fnStr) {
      const fnName = fnStr[1];

      // TODO: Investigate if we're able to type this differently.
      // @ts-ignore
      if (typeof window[`${fnName}`] === 'function') {
        const params = fnStr[2].replace(/["'()]/g, '').split(',');
        // @ts-ignore
        window[`${fnName}`].apply(undefined, params);
      }
    } else {
      throw new Error('No function string available');
    }
  }

  onForceSkipRegistration(flowPage?: number | null) {
    if (!flowPage) {
      useFlow().goToNextFlowPage();
    }

    if (flowPage) {
      useFlow().makeSpecificFlowPageActive(flowPage);
    }
  }

  authorSignature(): string {
    return 'Nicky Christensen | Dannie Hansen';
  }
}
